import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Copy,
  ExternalLink,
  Film,
  FolderOpen,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import { EmptyState } from '@/components/EmptyState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Button, buttonVariants } from '@/components/ui/Button';
import { Dropdown } from '@/components/ui/Dropdown';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Select';
import { googleDriveApi, studioApi, type StudioProject } from '@/lib/api';
import { DEFAULT_STUDIO_ASPECT_RATIO, STUDIO_ASPECT_RATIOS } from './studioAspectRatios';

function errorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { data?: { error?: string } } }).response;
    if (response?.data?.error) return response.data.error;
  }
  return error instanceof Error ? error.message : 'Wystąpił błąd.';
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function CreateProjectModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [labProductId, setLabProductId] = useState('');
  const [aspectRatio, setAspectRatio] = useState(DEFAULT_STUDIO_ASPECT_RATIO);

  const productsQuery = useQuery({
    queryKey: ['studio', 'lab-products'],
    queryFn: async () => (await studioApi.listLabProducts()).data.data,
    enabled: open,
  });

  const createMutation = useMutation({
    mutationFn: async () => (await studioApi.createProject({
      name,
      labProductId: labProductId || null,
      settings: {
        aspectRatio,
        snapEnabled: true,
      },
    })).data.data,
    onSuccess: async (project) => {
      await queryClient.invalidateQueries({ queryKey: ['studio', 'projects'] });
      setName('');
      setLabProductId('');
      setAspectRatio(DEFAULT_STUDIO_ASPECT_RATIO);
      onClose();
      navigate(`/studio/${project.id}`);
    },
  });

  const canSubmit = name.trim().length > 0 && !createMutation.isPending;

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Nowy projekt Studio"
      description="Projekt utworzy folder w Dysku Google: folder główny -> Studio -> nazwa projektu."
      footer={(
        <div className="flex items-center gap-2">
          <Button
            onClick={() => createMutation.mutate()}
            disabled={!canSubmit}
            loading={createMutation.isPending}
            leftIcon={<Plus />}
          >
            Utwórz projekt
          </Button>
          <Button variant="secondary" onClick={onClose} disabled={createMutation.isPending}>
            Anuluj
          </Button>
        </div>
      )}
    >
      <div className="space-y-4">
        <FormField label="Nazwa projektu" required error={createMutation.isError ? errorMessage(createMutation.error) : undefined}>
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Np. VSL produkt A - TOFU"
            autoFocus
          />
        </FormField>
        <FormField
          label="Produkt z Laboratorium"
          helper="Opcjonalnie. Ten wybór zasili zakładkę Laboratorium w Plikach."
        >
          <Select
            value={labProductId}
            onChange={(event) => setLabProductId(event.target.value)}
            placeholder={productsQuery.isLoading ? 'Ładowanie produktów...' : 'Bez produktu'}
            disabled={productsQuery.isLoading}
          >
            <option value="">Bez produktu</option>
            {(productsQuery.data || []).map(product => (
              <option key={product.id} value={product.id}>{product.name}</option>
            ))}
          </Select>
        </FormField>
        <FormField label="Proporcje projektu">
          <Select
            value={aspectRatio}
            onChange={(event) => setAspectRatio(event.target.value)}
          >
            {STUDIO_ASPECT_RATIOS.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </Select>
        </FormField>
      </div>
    </Modal>
  );
}

function RenameProjectModal({
  project,
  onClose,
}: {
  project: StudioProject | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(project?.name || '');

  useEffect(() => {
    setName(project?.name || '');
  }, [project]);

  const renameMutation = useMutation({
    mutationFn: async () => {
      if (!project) throw new Error('Brak projektu.');
      return (await studioApi.updateProject(project.id, { name })).data.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['studio', 'projects'] });
      onClose();
    },
  });

  return (
    <Modal
      isOpen={Boolean(project)}
      onClose={onClose}
      title="Zmień nazwę projektu"
      description="Nazwa folderu projektu na Dysku Google zostanie zaktualizowana."
      footer={(
        <div className="flex items-center gap-2">
          <Button
            onClick={() => renameMutation.mutate()}
            disabled={!name.trim() || renameMutation.isPending}
            loading={renameMutation.isPending}
          >
            Zapisz nazwę
          </Button>
          <Button variant="secondary" onClick={onClose} disabled={renameMutation.isPending}>
            Anuluj
          </Button>
        </div>
      )}
    >
      <FormField label="Nazwa projektu" error={renameMutation.isError ? errorMessage(renameMutation.error) : undefined}>
        <Input value={name} onChange={(event) => setName(event.target.value)} autoFocus />
      </FormField>
    </Modal>
  );
}

export function StudioPage() {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [renameProject, setRenameProject] = useState<StudioProject | null>(null);
  const [deleteProject, setDeleteProject] = useState<StudioProject | null>(null);

  const driveQuery = useQuery({
    queryKey: ['google-drive', 'status'],
    queryFn: async () => (await googleDriveApi.getStatus()).data.data,
  });
  const projectsQuery = useQuery({
    queryKey: ['studio', 'projects'],
    queryFn: async () => (await studioApi.listProjects()).data.data,
  });

  const duplicateMutation = useMutation({
    mutationFn: async (projectId: string) => (await studioApi.duplicateProject(projectId)).data.data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['studio', 'projects'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (projectId: string) => (await studioApi.deleteProject(projectId)).data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['studio', 'projects'] });
      setDeleteProject(null);
    },
  });

  const driveReady = Boolean(driveQuery.data?.connected && driveQuery.data?.rootFolderId);
  const projects = projectsQuery.data || [];

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Film}
        title="Studio"
        subtitle="Projekty wideo i montaż materiałów z uploadu oraz Laboratorium."
        actions={(
          <Button
            onClick={() => setCreateOpen(true)}
            disabled={!driveReady}
            leftIcon={<Plus />}
          >
            Nowy projekt
          </Button>
        )}
      />

      {!driveQuery.isLoading && !driveReady && (
        <div className="rounded-card border border-warning-border bg-warning-bg p-5 flex items-center justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-text-primary">Studio wymaga Dysku Google</h2>
            <p className="text-sm text-text-muted mt-1">
              Najpierw połącz integrację i wybierz folder główny. Nowe projekty będą zapisywane w strukturze Studio.
            </p>
          </div>
          <Link to="/integrations/google-drive" className={buttonVariants({ variant: 'secondary', size: 'sm' })}>
            Przejdź do integracji
          </Link>
        </div>
      )}

      {projectsQuery.isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
          {[1, 2, 3, 4, 5, 6].map(item => (
            <div key={item} className="h-40 rounded-card bg-surface-raised border border-border animate-pulse" />
          ))}
        </div>
      ) : projects.length === 0 ? (
        <div className="rounded-card border border-border bg-surface-raised">
          <EmptyState
            icon={Film}
            title="Brak projektów Studio"
            description={driveReady ? 'Utwórz pierwszy projekt i zacznij układać materiały na osi czasu.' : 'Połącz Dysk Google, żeby utworzyć pierwszy projekt.'}
            buttonLabel={driveReady ? 'Nowy projekt' : undefined}
            onButtonClick={driveReady ? () => setCreateOpen(true) : undefined}
            buttonIcon={Plus}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
          {projects.map(project => (
            <div
              key={project.id}
              className="group rounded-card border border-border bg-surface-raised hover:border-accent-primary/40 transition-colors overflow-hidden"
            >
              <Link
                to={`/studio/${project.id}`}
                className="aspect-video bg-surface-base border-b border-border-subtle flex items-center justify-center"
                aria-label={`Otwórz projekt ${project.name}`}
              >
                <Film className="w-12 h-12 text-text-disabled group-hover:text-accent-primary transition-colors" />
              </Link>
              <div className="p-3 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <Link to={`/studio/${project.id}`} className="block hover:text-accent-primary">
                      <h2 className="text-sm font-semibold text-text-primary truncate">{project.name}</h2>
                    </Link>
                    <p className="text-xs text-text-muted mt-1">
                      {project.labProduct?.name || 'Bez produktu Lab'} - {project._count?.assets || 0} assetów
                    </p>
                  </div>
                  <Dropdown
                    align="end"
                    portal
                    trigger={(
                      <button
                        type="button"
                        className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-text-muted hover:text-text-primary hover:bg-surface-hover border border-border-subtle"
                        aria-label="Menu projektu"
                      >
                        <MoreHorizontal className="w-4 h-4" />
                      </button>
                    )}
                    items={[
                      {
                        id: 'rename',
                        label: 'Zmień nazwę',
                        icon: <Pencil />,
                        onClick: () => setRenameProject(project),
                      },
                      {
                        id: 'duplicate',
                        label: duplicateMutation.isPending ? 'Duplikowanie...' : 'Duplikuj',
                        icon: <Copy />,
                        disabled: duplicateMutation.isPending,
                        onClick: () => duplicateMutation.mutate(project.id),
                      },
                      {
                        id: 'delete',
                        label: 'Usuń',
                        icon: <Trash2 />,
                        destructive: true,
                        divider: true,
                        onClick: () => setDeleteProject(project),
                      },
                    ]}
                  />
                </div>
                <div className="flex flex-col items-start gap-1 text-[10px] text-text-muted">
                  <span className="w-full truncate">Aktualizacja: {formatDate(project.updatedAt)}</span>
                  {project.driveFolderUrl && (
                    <a
                      href={project.driveFolderUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-accent-primary hover:text-text-primary"
                    >
                      <FolderOpen className="w-3.5 h-3.5" />
                      Drive
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {projectsQuery.isError && (
        <p className="text-sm text-danger">{errorMessage(projectsQuery.error)}</p>
      )}
      {duplicateMutation.isError && (
        <p className="text-sm text-danger">{errorMessage(duplicateMutation.error)}</p>
      )}

      <CreateProjectModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <RenameProjectModal project={renameProject} onClose={() => setRenameProject(null)} />
      <ConfirmDialog
        isOpen={Boolean(deleteProject)}
        title="Usunąć projekt?"
        message={deleteProject ? <>Projekt <strong>{deleteProject.name}</strong> zostanie usunięty ze Studio.</> : null}
        confirmLabel="Usuń projekt"
        onConfirm={() => deleteProject && deleteMutation.mutate(deleteProject.id)}
        onClose={() => setDeleteProject(null)}
        isPending={deleteMutation.isPending}
      />
    </div>
  );
}
